import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Shield, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

export const Home = () => {
  const features = [
    {
      icon: Heart,
      title: 'Empatik Yaklaşım',
      description: 'Her bireyin benzersiz olduğunu bilerek, kişiye özel çözümler sunuyoruz.'
    },
    {
      icon: Users,
      title: 'Uzman Kadro',
      description: 'Alanında deneyimli ve lisanslı psikologlarımızla profesyonel destek.'
    },
    {
      icon: Shield,
      title: 'Gizlilik Garantisi',
      description: 'Paylaştığınız her şey tamamen gizli ve güvenli bir ortamda kalır.'
    },
    {
      icon: Clock,
      title: 'Esnek Randevu',
      description: 'Size uygun saatlerde yüz yüze veya online danışmanlık seçenekleri.'
    }
  ];

  const benefits = [
    'Duygusal destek ve rehberlik',
    'Stres ve anksiyete yönetimi',
    'İlişki sorunlarında yardım',
    'Kişisel gelişim danışmanlığı',
    'Çocuk ve ergen psikolojisi',
    'Online terapi imkanı'
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center hero-gradient overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-beige-primary via-beige-primary/95 to-transparent z-10"></div>
          <img
            src="https://images.unsplash.com/photo-1573497491208-6b1acb260507?w=1200&q=80"
            alt="Kavi Psikolojik Danışmanlık"
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        
        <div className="container relative z-20">
          <div className="max-w-2xl fade-in">
            <div className="inline-block px-4 py-2 bg-sage-green/10 rounded-full mb-6">
              <span className="text-sage-green font-medium text-sm">Profesyonel Psikolojik Destek</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-forest-green mb-6 leading-tight">
              Zihinsel Sağlığınız İçin
              <span className="block text-sage-green mt-2">Güvenli Bir Liman</span>
            </h1>
            <p className="text-xl text-text-light mb-8 leading-relaxed">
              Hayatın zorluklarıyla başa çıkmak için profesyonel destek alın. Deneyimli psikologlarımız, size özel çözümler sunarak yaşam kalitenizi artırmanıza yardımcı olur.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-sage-green hover:bg-forest-green text-white button-hover text-base px-8 py-6">
                  Randevu Alın
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-2 border-sage-green text-sage-green hover:bg-sage-green hover:text-white button-hover text-base px-8 py-6">
                  Hizmetlerimiz
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-forest-green mb-4">Neden Bizi Seçmelisiniz?</h2>
            <p className="text-lg text-text-light max-w-2xl mx-auto">
              Psikolojik sağlığınız için en iyi desteği almayı hak ediyorsunuz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="p-8 border-none shadow-lg card-hover bg-gradient-to-br from-white to-beige-primary/30">
                  <div className="w-14 h-14 bg-gradient-to-br from-sage-green to-mint-green rounded-2xl flex items-center justify-center mb-6 transform hover:scale-110 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-forest-green mb-3">{feature.title}</h3>
                  <p className="text-text-light leading-relaxed">{feature.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-gradient-to-br from-beige-primary to-beige-secondary">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1637245048732-adf1a547835e?w=800&q=80"
                  alt="Kavi Danışmanlık Ortamı"
                  className="w-full h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-gradient-to-br from-sage-green/20 to-mint-green/20 rounded-3xl -z-0"></div>
              <div className="absolute -top-8 -left-8 w-48 h-48 bg-gradient-to-br from-forest-green/10 to-sage-green/10 rounded-full -z-0"></div>
            </div>

            <div>
              <h2 className="text-4xl font-bold text-forest-green mb-6">
                Size Nasıl Yardımcı Olabiliriz?
              </h2>
              <p className="text-lg text-text-light mb-8 leading-relaxed">
                Psikolojik danışmanlık sürecinde, yaşadığınız zorlukları anlamak ve çözüm yolları geliştirmek için birlikte çalışırız.
              </p>

              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3 group">
                    <div className="mt-1 flex-shrink-0">
                      <CheckCircle2 className="w-6 h-6 text-sage-green group-hover:scale-110 transition-transform" />
                    </div>
                    <p className="text-text-dark font-medium">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/about">
                  <Button size="lg" className="bg-forest-green hover:bg-sage-green text-white button-hover">
                    Daha Fazla Bilgi
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-forest-green/95 to-sage-green/95 z-10"></div>
          <img
            src="https://images.pexels.com/photos/7176137/pexels-photo-7176137.jpeg?w=1200&q=80"
            alt="Kavi Danışmanlık"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="container relative z-20 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            İlk Adımı Atmaya Hazır mısınız?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
            Profesyonel destek almanın tam zamanı. Size en uygun randevu için hemen bizimle iletişime geçin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-sage-green hover:bg-beige-primary button-hover text-base px-8 py-6">
                Randevu Al
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+905305128090">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-sage-green button-hover text-base px-8 py-6">
                Hemen Ara: 0530 512 80 90
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};